/**
 * squareInvoicePayload — Square 請求書（square_create_invoice.php）に送る中身を予約から作る（2026-10-31 切り出し）
 *
 * サイドパネルの［請求書を発行］（useSquareInvoice）と AI エージェントの create_square_invoice が
 * 同じ中身を送るように1か所にまとめた。片方だけ直して金額や補償の出し方がずれるのを防ぐ。
 * safe はエディタ形（toEditorReservation の出力）。
 */
import type { EditorReservation } from '../components/editor2/types';
import { isCdwBundled, isNocBundled } from './cdwBundle';

export function buildSquareInvoicePayload(safe: EditorReservation, sqNum: number) {
  return {
    // Square 設定番号(那覇=0 / 宮古=2)。省略すると server 側が 0 を既定にするため、
    // 宮古の請求書が那覇の Square に作られてしまう。必ず明示する。
    sqNum,
    id: safe.id,
    contact_code: safe.contact_code,
    customer_name: safe.customer_name,
    customer_email: safe.customer_email,
    customer_tel: safe.customer_tel,
    customer_address: safe.customer_address,
    start_date: safe.start_date,
    end_date: safe.end_date,
    car_type: safe.car_type,
    vehicle_id: safe.vehicle_id,
    status: safe.status,
    bg_color: safe.bg_color,
    text_color: safe.text_color,
    booking_date: safe.booking_date,
    rental_location: safe.rental_location,
    return_location: safe.return_location,
    pu_staff_id: safe.pu_staff_id,
    do_staff_id: safe.do_staff_id,
    rent_staff_id: safe.rent_staff_id,
    return_staff_id: safe.return_staff_id,
    mail_send_flg1: safe.mail_send_flg1,
    mail_send_flg2: safe.mail_send_flg2,
    mail_send_flg3: safe.mail_send_flg3,
    short_memo: safe.short_memo,
    json_data: safe.json_data,
    days: safe.days,
    nights: safe.nights,
    detail: {
      contact_id: safe.contact_id || safe.contact_code,
      contact_media: safe.contact_media,
      contact_plan: safe.contact_plan,
      communication_tools: safe.communication_tools,
      pax: safe.pax,
      pax_children: safe.pax_children,
      child_seat: safe.child_seat,
      booster_seat: safe.booster_seat,
      junior_seat: safe.junior_seat,
      arrival_flight: safe.arrival_flight,
      departure_flight: safe.departure_flight,
      base_price: safe.base_price,
      option: safe.option,
      option_price: safe.option_price,
      // NOC 込みプラン（宮古の繁体字・韓国語。noc_price=0・base_price に NOC を含む）も加入として送る。
      // PHP は json_data の noc_bundled を見て NOC 行を出さず品名に (CDW・NOC) を付ける（2026-10-21）
      noc: isNocBundled(safe.json_data) || (safe.noc_price ?? 0) > 0,
      noc_price: safe.noc_price,
      // CDW 込みプラン（cdw_price=0・base_price に CDW を含む）は加入として送る。PHP は json_data の
      // cdw_bundled を見て CDW 行を出さず品名に (CDW) を付ける（ここを価格基準のままにすると未加入扱いになり、
      // 繁体字・韓国語の請求書に「補償未加入」の案内が出る）。正: booking-platform/md/options.md
      cdw: isCdwBundled(safe.json_data) || (safe.cdw_price ?? 0) > 0,
      cdw_price: safe.cdw_price,
      coupon: safe.coupon,
      point: safe.point,
      sum_price: safe.sum_price,
      days: safe.days,
      nights: safe.nights,
      invoice_link: safe.invoice_link,
      comment: safe.comment,
      memo: safe.memo,
      emergency_name: safe.emergency_name,
      emergency_rel: safe.emergency_rel,
      emergency_tel: safe.emergency_tel,
      license: safe.license,
      payment: safe.payment,
      terms_and_conditions: safe.terms_and_conditions,
      disclaimer: safe.disclaimer,
      agreement: safe.agreement,
    },
  };
}
